import type { LibraryItem, LibraryItemKind } from "@/components/library/library-item"

/** Колонки табличного вида, по которым кликается шапка. */
export type LibrarySortColumn = "name" | "type" | "duration" | "date"

export type LibrarySortState = { id: LibrarySortColumn; desc: boolean }

/** Новые сверху, пока человек не выбрал другую колонку. */
export const LIBRARY_SORT_DEFAULT: LibrarySortState = { id: "date", desc: true }

export type LibrarySortOptions = {
  locale: string
  typeLabel: Record<LibraryItemKind, string>
}

/**
 * Длина, которую показывает колонка «Хронометраж».
 * У подрезанного клипа (сегмент аниматика) это диапазон, а не весь файл.
 * Пустое и неположительное — не длина: в сортировке такие строки уходят в конец.
 */
export function libraryItemDurationMs(item: LibraryItem): number | null {
  const asset = item.asset
  const trimmed =
    asset?.sourceInMs != null && asset?.sourceOutMs != null && asset.sourceOutMs > asset.sourceInMs
      ? asset.sourceOutMs - asset.sourceInMs
      : null
  const ms = trimmed ?? asset?.durationMs
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms <= 0) return null
  return ms
}

/**
 * Первое включение колонки — по убыванию, повторный клик переключает направление.
 * Так же устроен журнал биллинга.
 */
export function toggleLibrarySort(previous: LibrarySortState, column: LibrarySortColumn): LibrarySortState {
  if (previous.id === column) return { id: column, desc: !previous.desc }
  return { id: column, desc: true }
}

/**
 * Порядок строк библиотеки. Пустая дата и пустой хронометраж всегда в конце.
 * При равном значении сохраняется исходный порядок — кадры одной версии не разъезжаются.
 */
export function sortLibraryItems(
  items: LibraryItem[],
  sort: LibrarySortState,
  options: LibrarySortOptions,
): LibraryItem[] {
  const indexed = items.map((item, index) => ({ item, index }))
  indexed.sort((a, b) => {
    const compared = compareLibraryItems(a.item, b.item, sort, options)
    if (compared !== 0) return compared
    return a.index - b.index
  })
  return indexed.map((row) => row.item)
}

function compareLibraryItems(
  a: LibraryItem,
  b: LibraryItem,
  sort: LibrarySortState,
  options: LibrarySortOptions,
): number {
  switch (sort.id) {
    case "name":
      return compareText(a.label, b.label, options.locale, sort.desc)
    case "type":
      return compareText(options.typeLabel[a.kind], options.typeLabel[b.kind], options.locale, sort.desc)
    case "duration":
      return compareNullableNumber(libraryItemDurationMs(a), libraryItemDurationMs(b), sort.desc)
    case "date":
      return compareNullableNumber(createdAtMs(a), createdAtMs(b), sort.desc)
  }
}

function createdAtMs(item: LibraryItem): number | null {
  if (!item.createdAt) return null
  const ms = Date.parse(item.createdAt)
  return Number.isFinite(ms) ? ms : null
}

/** Пустое значение не участвует в направлении: оно всегда ниже заполненного. */
function compareNullableNumber(a: number | null, b: number | null, desc: boolean): number {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  return desc ? b - a : a - b
}

function compareText(a: string, b: string, locale: string, desc: boolean): number {
  const compared = a.localeCompare(b, locale, { sensitivity: "accent" })
  return desc ? -compared : compared
}
